/**
 * Broadcast functionality for groups and events
 * Sends notifications and SMS to all members/attendees
 */

import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { createNotification } from './notifications';
import { sendSMS } from './sms';
import { canSendSMS } from './sms-consent';
import { getGroupMembers } from './firestore/groups';

export interface BroadcastOptions {
  groupId?: string;
  eventId?: string;
  message: string;
  senderId: string;
  senderName: string;
}

/**
 * Send broadcast to group members
 */
const sendGroupBroadcast = async (groupId: string, message: string, senderId: string, senderName: string) => {
  // Get group info
  const groupDoc = await getDoc(doc(db, 'groups', groupId));
  if (!groupDoc.exists()) {
    throw new Error('Group not found');
  }
  const group = groupDoc.data();
  const groupName = group.name || 'Group';

  // Get all members
  const members = await getGroupMembers(groupId);
  
  // Get user data for each member to send SMS
  const memberPromises = members.map(async (member) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', member.userId));
      if (!userDoc.exists()) return null;
      return { ...member, userData: userDoc.data() };
    } catch (error) {
      console.error(`Error loading user data for ${member.userId}:`, error);
      return null;
    }
  });

  const membersWithData = (await Promise.all(memberPromises)).filter(Boolean);

  // Send notifications and SMS to each member
  const broadcastPromises = membersWithData.map(async (member) => {
    const promises = [];

    // Send notification
    promises.push(
      createNotification({
        userId: member.userId,
        type: 'group_invite', // Using existing type, could add 'broadcast' type later
        title: `📢 Broadcast from ${groupName}`,
        message: `${senderName}: ${message}`,
        groupId,
        fromUserId: senderId,
        status: 'unread',
      })
    );

    // Send SMS if user has consent
    if (member.userData?.phone && member.userData?.smsConsent) {
      try {
        const canSend = await canSendSMS(member.userId, senderId);
        if (canSend) {
          const smsMessage = `📢 ${groupName}: ${message}`;
          promises.push(
            sendSMS({
              to: member.userData.phone,
              message: smsMessage,
            }).catch((smsError) => {
              console.error(`Failed to send SMS to ${member.userId}:`, smsError);
              // Don't fail the whole broadcast if SMS fails
            })
          );
        }
      } catch (smsError) {
        console.error(`SMS consent check failed for ${member.userId}:`, smsError);
        // Continue without SMS
      }
    }

    return Promise.allSettled(promises);
  });

  await Promise.all(broadcastPromises);
};

/**
 * Send broadcast to event attendees
 */
const sendEventBroadcast = async (eventId: string, message: string, senderId: string, senderName: string) => {
  // Get event info
  const eventDoc = await getDoc(doc(db, 'events', eventId));
  if (!eventDoc.exists()) {
    throw new Error('Event not found');
  }
  const event = eventDoc.data();
  const eventTitle = event.title || 'Event';

  // Get all RSVPs (attendees)
  const rsvpsRef = collection(db, 'events', eventId, 'rsvps');
  const rsvpsSnapshot = await getDocs(rsvpsRef);
  
  // Get user data for each attendee
  const attendeePromises = rsvpsSnapshot.docs.map(async (rsvpDoc) => {
    const userId = rsvpDoc.id;
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) return null;
      return { userId, userData: userDoc.data() };
    } catch (error) {
      console.error(`Error loading user data for ${userId}:`, error);
      return null;
    }
  });

  const attendeesWithData = (await Promise.all(attendeePromises)).filter(Boolean);

  // Send notifications and SMS to each attendee
  const broadcastPromises = attendeesWithData.map(async (attendee) => {
    const promises = [];

    // Send notification
    promises.push(
      createNotification({
        userId: attendee.userId,
        type: 'event_invite', // Using existing type
        title: `📢 Broadcast from ${eventTitle}`,
        message: `${senderName}: ${message}`,
        eventId,
        fromUserId: senderId,
        status: 'unread',
      })
    );

    // Send SMS if user has consent
    if (attendee.userData?.phone && attendee.userData?.smsConsent) {
      try {
        const canSend = await canSendSMS(attendee.userId, senderId);
        if (canSend) {
          const smsMessage = `📢 ${eventTitle}: ${message}`;
          promises.push(
            sendSMS({
              to: attendee.userData.phone,
              message: smsMessage,
            }).catch((smsError) => {
              console.error(`Failed to send SMS to ${attendee.userId}:`, smsError);
              // Don't fail the whole broadcast if SMS fails
            })
          );
        }
      } catch (smsError) {
        console.error(`SMS consent check failed for ${attendee.userId}:`, smsError);
        // Continue without SMS
      }
    }

    return Promise.allSettled(promises);
  });

  await Promise.all(broadcastPromises);
};

/**
 * Send broadcast to group or event
 */
export const sendBroadcast = async (options: BroadcastOptions): Promise<void> => {
  const { groupId, eventId, message, senderId, senderName } = options;

  if (!groupId && !eventId) {
    throw new Error('Either groupId or eventId must be provided');
  }

  if (groupId) {
    await sendGroupBroadcast(groupId, message, senderId, senderName);
  } else if (eventId) {
    await sendEventBroadcast(eventId, message, senderId, senderName);
  }
};
